/**
 * Forecast Hierarchy API Service
 * 
 * Handles all API calls related to forecasting hierarchy
 */
import { apiService as api } from './api.service';

// API endpoints
const ENDPOINTS = {
  // Hierarchy Management
  HIERARCHIES: '/forecasting-hierarchy/hierarchies',
  HIERARCHY_BY_ID: (id) => `/forecasting-hierarchy/hierarchies/${id}`,
  HIERARCHY_LEVELS: (id) => `/forecasting-hierarchy/hierarchies/${id}/levels`,
  HIERARCHY_DEFINITIONS: '/forecasting-hierarchy/hierarchies/definitions',
  HIERARCHY_SNAPSHOT: '/forecasting-hierarchy/hierarchies/snapshot',
  HIERARCHY_STATUS: (id) => `/forecasting-hierarchy/hierarchies/${id}/status`,
  HIERARCHY_VALIDATION: (id) => `/forecasting-hierarchy/hierarchies/${id}/validation`,
  
  // Node Management
  NODES_AT_LEVEL: (hierarchyId, levelId) => `/forecasting-hierarchy/hierarchies/${hierarchyId}/levels/${levelId}/nodes`,
  CHILD_NODES: (hierarchyId, nodeId) => `/forecasting-hierarchy/nodes/${nodeId}/children`,
  NODE_DETAILS: (nodeId) => `/forecasting-hierarchy/nodes/${nodeId}`,
  NODE_BY_KEY: (hierarchyId, nodeKey) => `/forecasting-hierarchy/nodes/${nodeKey}/by-key`,
  
  // Value Retrieval
  NODE_VALUES: (hierarchyId, nodeId) => `/forecasting-hierarchy/nodes/${nodeId}/values`,
  NODE_VALUES_SUMMARY: (hierarchyId, nodeId) => `/forecasting-hierarchy/nodes/${nodeId}/values/summary`,
  NODE_VALUES_PERIODS: (hierarchyId, nodeId) => `/forecasting-hierarchy/nodes/${nodeId}/values/periods`,
  TOP_LINE_VALUES: '/forecasting-hierarchy/values/top-line',
  
  // Upload and Status
  UPLOAD_AGGREGATED_DATA: '/forecasting-hierarchy/hierarchies/upload',
  UPLOAD_STATUS: '/forecasting-hierarchy/hierarchies/status'
};

/**
 * Forecast Hierarchy API functions
 */
export const forecastHierarchyService = {
  // ============================================================================
  // HIERARCHY MANAGEMENT
  // ============================================================================
  
  /**
   * Get all available hierarchies
   * @param {Object} params - Query parameters
   * @param {string} params.hierarchy_type - Filter by hierarchy type (location, product, attribute)
   * @param {boolean} params.include_inactive - Include inactive hierarchies
   * @returns {Promise<Object>} Hierarchies data
   */
  getHierarchies: (params = {}) => api.get(ENDPOINTS.HIERARCHIES, { params }),
  
  /**
   * Get hierarchy by ID
   * @param {number} hierarchyId - Hierarchy ID
   * @returns {Promise<Object>} Hierarchy details
   */
  getHierarchyById: (hierarchyId) => api.get(ENDPOINTS.HIERARCHY_BY_ID(hierarchyId)),
  
  /**
   * Get hierarchy levels
   * @param {number} hierarchyId - Hierarchy ID
   * @returns {Promise<Object>} Hierarchy levels
   */
  getHierarchyLevels: (hierarchyId) => api.get(ENDPOINTS.HIERARCHY_LEVELS(hierarchyId)),
  
  /**
   * Get hierarchy definitions for external services
   * @param {Object} params - Query parameters
   * @param {string} params.hierarchy_ids - Comma-separated hierarchy IDs
   * @param {boolean} params.include_inactive - Include inactive hierarchies
   * @returns {Promise<Object>} Hierarchy definitions
   */
  getHierarchyDefinitions: (params = {}) => api.get(ENDPOINTS.HIERARCHY_DEFINITIONS, { params }),
  
  /**
   * Get hierarchy snapshot
   * @param {string} runId - Forecast run ID
   * @param {number} hierarchyId - Hierarchy ID
   * @returns {Promise<Object>} Hierarchy snapshot
   */
  getHierarchySnapshot: (runId, hierarchyId) => api.get(ENDPOINTS.HIERARCHY_SNAPSHOT, {
    params: { run_id: runId, hierarchy_id: hierarchyId }
  }),
  
  /**
   * Get hierarchy status
   * @param {number} hierarchyId - Hierarchy ID
   * @returns {Promise<Object>} Hierarchy status
   */
  getHierarchyStatus: (hierarchyId) => api.get(ENDPOINTS.HIERARCHY_STATUS(hierarchyId)),
  
  /**
   * Validate hierarchy
   * @param {number} hierarchyId - Hierarchy ID
   * @returns {Promise<Object>} Validation results
   */
  validateHierarchy: (hierarchyId) => api.get(ENDPOINTS.HIERARCHY_VALIDATION(hierarchyId)),
  
  // ============================================================================
  // NODE MANAGEMENT
  // ============================================================================
  
  /**
   * Get nodes at a specific level
   * @param {number} hierarchyId - Hierarchy ID
   * @param {number} levelId - Level ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Maximum number of nodes
   * @param {number} params.offset - Number of nodes to skip
   * @param {string} params.search - Search term for node name or key
   * @param {boolean} params.non_empty_only - Show only nodes with forecast values
   * @param {string} params.run_id - Run ID for non-empty filter
   * @returns {Promise<Object>} Nodes data
   */
  getNodesAtLevel: (hierarchyId, levelId, params = {}) => api.get(
    ENDPOINTS.NODES_AT_LEVEL(hierarchyId, levelId), 
    { params }
  ),
  
  /**
   * Get child nodes for a parent node
   * @param {number} hierarchyId - Hierarchy ID (not used in URL but kept for compatibility)
   * @param {number} nodeId - Parent node ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Maximum number of child nodes
   * @param {number} params.offset - Number of child nodes to skip
   * @param {boolean} params.non_empty_only - Show only nodes with forecast values
   * @param {string} params.run_id - Run ID for non-empty filter
   * @returns {Promise<Object>} Child nodes data
   */
  getChildNodes: (hierarchyId, nodeId, params = {}) => api.get(ENDPOINTS.CHILD_NODES(hierarchyId, nodeId), { params }),
  
  /**
   * Get node details
   * @param {number} hierarchyId - Hierarchy ID (not used in URL but kept for compatibility)
   * @param {number} nodeId - Node ID
   * @returns {Promise<Object>} Node details
   */
  getNodeDetails: (hierarchyId, nodeId) => api.get(ENDPOINTS.NODE_DETAILS(nodeId)),
  
  /**
   * Get node by key
   * @param {number} hierarchyId - Hierarchy ID (not used in URL but kept for compatibility)
   * @param {string} nodeKey - Node key
   * @param {Object} params - Query parameters
   * @param {number} params.level_id - Optional level ID to filter
   * @returns {Promise<Object>} Node details
   */
  getNodeByKey: (hierarchyId, nodeKey, params = {}) => api.get(
    ENDPOINTS.NODE_BY_KEY(hierarchyId, nodeKey), 
    { params }
  ),
  
  // ============================================================================
  // VALUE RETRIEVAL
  // ============================================================================
  
  /**
   * Get node values (time series)
   * @param {number} hierarchyId - Hierarchy ID (not used in URL but kept for compatibility)
   * @param {number} nodeId - Node ID
   * @param {Object} params - Query parameters
   * @param {string} params.run_id - Forecast run ID
   * @param {string} params.start_date - Start date filter (YYYY-MM-DD)
   * @param {string} params.end_date - End date filter (YYYY-MM-DD)
   * @param {boolean} params.include_reference - Include reference run data
   * @returns {Promise<Object>} Node values data
   */
  getNodeValues: (hierarchyId, nodeId, params = {}) => api.get(
    ENDPOINTS.NODE_VALUES(hierarchyId, nodeId), 
    { params }
  ),
  
  /**
   * Get node values summary
   * @param {number} hierarchyId - Hierarchy ID (not used in URL but kept for compatibility)
   * @param {number} nodeId - Node ID
   * @param {Object} params - Query parameters
   * @param {string} params.run_id - Forecast run ID
   * @param {string} params.period_type - Summary period type (daily, weekly, monthly)
   * @returns {Promise<Object>} Node values summary
   */
  getNodeValuesSummary: (hierarchyId, nodeId, params = {}) => api.get(
    ENDPOINTS.NODE_VALUES_SUMMARY(hierarchyId, nodeId), 
    { params }
  ),
  
  /**
   * Get node values by period
   * @param {number} hierarchyId - Hierarchy ID (not used in URL but kept for compatibility)
   * @param {number} nodeId - Node ID
   * @param {Object} params - Query parameters
   * @param {string} params.run_id - Forecast run ID
   * @param {string} params.period_type - Period type (daily, weekly, monthly)
   * @param {string} params.start_date - Start date filter
   * @param {string} params.end_date - End date filter
   * @returns {Promise<Object>} Node values by period
   */
  getNodeValuesByPeriod: (hierarchyId, nodeId, params = {}) => api.get(
    ENDPOINTS.NODE_VALUES_PERIODS(hierarchyId, nodeId), 
    { params }
  ),
  
  /**
   * Get top-line values for a forecast run
   * @param {string} runId - Forecast run ID
   * @param {Object} params - Query parameters
   * @param {string} params.start_date - Start date filter (YYYY-MM-DD)
   * @param {string} params.end_date - End date filter (YYYY-MM-DD)
   * @returns {Promise<Object>} Top-line values data
   */
  getTopLineValues: (runId, params = {}) => api.get(ENDPOINTS.TOP_LINE_VALUES, {
    params: { run_id: runId, ...params }
  }),
  
  // ============================================================================
  // UPLOAD AND STATUS
  // ============================================================================
  
  /**
   * Upload aggregated forecast data
   * @param {Object} uploadData - Aggregated forecast data
   * @returns {Promise<Object>} Upload result
   */
  uploadAggregatedData: (uploadData) => api.post(ENDPOINTS.UPLOAD_AGGREGATED_DATA, uploadData),
  
  /**
   * Get upload status
   * @param {string} runId - Forecast run ID
   * @param {number} hierarchyId - Optional hierarchy ID
   * @returns {Promise<Object>} Upload status
   */
  getUploadStatus: (runId, hierarchyId = null) => {
    const params = { run_id: runId };
    if (hierarchyId) {
      params.hierarchy_id = hierarchyId;
    }
    return api.get(ENDPOINTS.UPLOAD_STATUS, { params });
  }
};

export default forecastHierarchyService; 